import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import * as trainersApi from '../../api/trainers'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Spinner from '../../components/common/Spinner'
import Badge from '../../components/common/Badge'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  UsersIcon,
  EnvelopeIcon,
  PhoneIcon
} from '@heroicons/react/24/outline'

export default function TrainersOverview() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { data, isLoading } = useQuery({
    queryKey: ['trainers', { search: searchQuery, status: statusFilter }],
    queryFn: () => trainersApi.getTrainers({ 
      search: searchQuery,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit: 1000
    }),
  })

  const trainers = data?.data || []

  // Filter trainers locally for instant feedback
  const filteredTrainers = trainers.filter(trainer => {
    const fullName = `${trainer.first_name || ''} ${trainer.last_name || ''}`.trim()
    const matchesSearch = !searchQuery ||
      fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trainer.email?.toLowerCase().includes(searchQuery.toLowerCase())
    
    return matchesSearch
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trainers</h1>
          <p className="mt-2 text-gray-600">
            {filteredTrainers.length} trainer{filteredTrainers.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <Link to="/trainers/new">
          <Button variant="primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Trainer
          </Button>
        </Link>
      </div>

      {/* Search and Filters */}
      <Card>
        <Card.Body>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search trainers by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Trainers Grid */}
      {filteredTrainers.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No trainers found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery 
                ? 'Try adjusting your search criteria' 
                : 'Get started by creating a new trainer'}
            </p>
            {!searchQuery && (
              <Link to="/trainers/new" className="inline-block mt-4">
                <Button variant="primary">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Trainer
                </Button>
              </Link>
            )}
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrainers.map((trainer) => (
            <Card key={trainer.id} className="hover:shadow-lg transition-shadow">
              <Card.Body>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {`${trainer.first_name || ''} ${trainer.last_name || ''}`.trim() || 'Unnamed Trainer'}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">{trainer.uid}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <UsersIcon className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {trainer.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                      <span className="truncate">{trainer.email}</span>
                    </div>
                  )}
                  {trainer.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      <span>{trainer.phone}</span>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Link to={`/trainers/${trainer.id}`}>
                    <Button variant="secondary" size="sm" className="w-full">
                      View Details
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}